/*
 * Portfolio Activation Center (Part 3). Portfolio Manager invites one primary
 * contact per licensed school, sends tokenized invite links (copyable), and
 * monitors activation + Diagnostic progress. Lives under School Onboarding.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "./shared";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, UserPlus, Send, Upload, Copy, Check, RefreshCw, School, Users, Mail, CheckCircle2, Activity, AlertTriangle } from "lucide-react";

const SCHOOL_ROLES = ["School Tenant Admin", "Executive Director / CEO", "Principal", "Business Administrator", "Board Chair", "Diagnostic Lead", "Reviewer / Approver", "Contributor", "Read Only"];
const STATUS_COLOR = {
  "Contact Needed": "bg-gray-200 text-gray-700", "Invite Ready": "bg-blue-100 text-blue-800",
  Invited: "bg-indigo-100 text-indigo-800", "Invite Expired": "bg-amber-100 text-amber-800",
  Accepted: "bg-teal-100 text-teal-800", "Workspace Active": "bg-emerald-100 text-emerald-800",
  "Diagnostic Started": "bg-emerald-100 text-emerald-800", "Diagnostic Submitted": "bg-purple-100 text-purple-800",
  Completed: "bg-green-100 text-green-800",
};

function parseCSV(text) {
  const rows = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  for (const line of lines) {
    const cells = []; let cur = ""; let q = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
      else if (ch === "," && !q) { cells.push(cur); cur = ""; }
      else cur += ch;
    }
    cells.push(cur);
    rows.push(cells.map((c) => c.trim()));
  }
  return rows;
}

const Card = ({ icon: Icon, label, value, tone }) => (
  <div className="rounded-xl border border-[#e5e7eb] bg-white p-4">
    <div className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${tone || "bg-slate-100 text-slate-600"}`}><Icon className="h-4 w-4" /></div>
    <div className="mt-2 text-2xl font-bold text-[#111827]">{value}</div>
    <div className="text-[12px] text-[#6b7280]">{label}</div>
  </div>
);

export default function ActivationCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [links, setLinks] = useState({}); // workspace_id -> link
  const [copied, setCopied] = useState("");
  const [contactFor, setContactFor] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", title: "", school_role: "School Tenant Admin", phone: "" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [sendAfter, setSendAfter] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/portfolio-activation/me`, { withCredentials: true });
      setData(r.data);
    } catch { toast.error("Failed to load activation center"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveContact = async () => {
    if (!form.first_name || !form.last_name || !form.email) { toast.error("Name and email required"); return; }
    setBusy("contact");
    try {
      await axios.post(`${API}/api/portfolio-activation/contact`, { ...form, school_workspace_id: contactFor.id }, { withCredentials: true });
      toast.success("Contact added"); setContactFor(null);
      setForm({ first_name: "", last_name: "", email: "", title: "", school_role: "School Tenant Admin", phone: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };

  const registerLinks = (invites) => {
    if (!invites?.length) return;
    setLinks((prev) => { const n = { ...prev }; invites.forEach((i) => { n[i.school_workspace_id] = window.location.origin + i.invite_link; }); return n; });
  };

  const sendInvite = async (wid) => {
    setBusy(wid);
    try {
      const r = await axios.post(`${API}/api/portfolio-activation/resend/${wid}`, {}, { withCredentials: true });
      registerLinks([r.data.invite]); toast.success("Invite link generated"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add a contact first"); } finally { setBusy(""); }
  };

  const sendAllReady = async () => {
    setBusy("all");
    try {
      const r = await axios.post(`${API}/api/portfolio-activation/invite`, {}, { withCredentials: true });
      registerLinks(r.data.invites); toast.success(`${r.data.sent} invitation link(s) generated`); load();
    } catch { toast.error("Failed"); } finally { setBusy(""); }
  };

  const runBulk = async () => {
    const rows = parseCSV(bulkText);
    if (rows.length < 2) { toast.error("Paste a CSV with a header row and at least one contact"); return; }
    const header = rows[0].map((h) => h.toLowerCase());
    const idx = (names) => names.map((n) => header.indexOf(n)).find((i) => i >= 0);
    const cSchool = idx(["school name", "school_name"]);
    const cNces = idx(["nces school id", "nces_school_id", "nces"]);
    const cFirst = idx(["contact first name", "first_name", "first name"]);
    const cLast = idx(["contact last name", "last_name", "last name"]);
    const cEmail = idx(["contact email", "email"]);
    const cTitle = idx(["contact title", "title"]);
    const cPhone = idx(["contact phone", "phone"]);
    const cRole = idx(["school role", "school_role", "role"]);
    const cState = idx(["state"]);
    const payload = rows.slice(1).map((r) => ({
      school_name: r[cSchool] || "", nces_school_id: cNces >= 0 ? r[cNces] : "",
      state: cState >= 0 ? r[cState] : "", first_name: r[cFirst] || "", last_name: r[cLast] || "",
      email: r[cEmail] || "", title: cTitle >= 0 ? r[cTitle] : "", phone: cPhone >= 0 ? r[cPhone] : "",
      school_role: cRole >= 0 ? r[cRole] : "School Tenant Admin",
    })).filter((x) => x.email && x.school_name);
    if (!payload.length) { toast.error("No valid rows found. Check your columns."); return; }
    setBusy("bulk");
    try {
      const res = await axios.post(`${API}/api/portfolio-activation/contacts/bulk`, { rows: payload, send_after: sendAfter }, { withCredentials: true });
      setBulkResult(res.data); registerLinks(res.data.invites); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); } finally { setBusy(""); }
  };

  const copyLink = (wid) => { navigator.clipboard.writeText(links[wid]); setCopied(wid); setTimeout(() => setCopied(""), 2000); };

  if (loading) return <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  if (!data?.portfolio) return <div className="py-20 text-center text-slate-500" data-testid="activation-no-portfolio">Your Activation Center will appear here once your portfolio licenses are purchased.</div>;

  const s = data.summary;
  const schools = data.schools || [];

  return (
    <div data-testid="activation-center">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-[#111827]">Activate Your Schools</h1>
        <p className="text-sm text-[#6b7280] mt-1 max-w-2xl">Invite each school's primary contact so they can access their Diagnostic workspace, complete setup, and begin MySchoolRx.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Card icon={School} label="Schools Licensed" value={s.schools_licensed} tone="bg-slate-100 text-slate-700" />
        <Card icon={Users} label="Contacts Added" value={s.contacts_added} tone="bg-blue-100 text-blue-700" />
        <Card icon={Mail} label="Invitations Sent" value={s.invitations_sent} tone="bg-indigo-100 text-indigo-700" />
        <Card icon={CheckCircle2} label="Workspaces Active" value={s.workspaces_active} tone="bg-emerald-100 text-emerald-700" />
        <Card icon={Activity} label="Diagnostics Started" value={s.diagnostics_started} tone="bg-teal-100 text-teal-700" />
        <Card icon={CheckCircle2} label="Diagnostics Completed" value={s.diagnostics_completed} tone="bg-green-100 text-green-700" />
        <Card icon={AlertTriangle} label="Needs Attention" value={s.needs_attention} tone="bg-amber-100 text-amber-700" />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Button onClick={sendAllReady} disabled={busy === "all"} className="bg-red-700 hover:bg-red-800 text-white" data-testid="activation-send-all">
          {busy === "all" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send All Ready Invitations
        </Button>
        <Button variant="outline" onClick={() => { setBulkOpen(true); setBulkResult(null); }} data-testid="activation-bulk-open"><Upload className="h-4 w-4 mr-2" />Upload Contacts CSV</Button>
        <Button variant="ghost" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
      </div>

      <div className="rounded-xl border border-[#e5e7eb] bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="activation-table">
            <thead className="bg-[#f9fafb] text-[#6b7280] text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">School</th>
                <th className="text-left px-4 py-2.5 font-semibold">Primary Contact</th>
                <th className="text-left px-4 py-2.5 font-semibold">Activation Status</th>
                <th className="text-left px-4 py-2.5 font-semibold">Diagnostic</th>
                <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {schools.map((w) => (
                <tr key={w.id} className="border-t border-[#f0f1f3] hover:bg-[#fafbfc]" data-testid={`activation-row-${w.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-[#111827]">{w.school_name_snapshot}</div>
                    <div className="text-[12px] text-[#6b7280]">{w.city ? `${w.city}, ` : ""}{w.state}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    {w.contact ? <div><div className="text-[#111827]">{w.contact.first_name} {w.contact.last_name}</div><div className="text-[12px] text-[#6b7280]">{w.contact.email}</div></div>
                      : <span className="text-[12px] text-amber-700 font-medium">Contact Needed</span>}
                  </td>
                  <td className="px-4 py-2.5"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${STATUS_COLOR[w.activation_status] || "bg-gray-100"}`}>{w.activation_status}</span></td>
                  <td className="px-4 py-2.5 text-[#6b7280]">{(w.diagnostic_status || "not_started").replace("_", " ")}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      {!w.tenant_id && (
                        <Button size="sm" variant="outline" className="h-7" onClick={() => { setContactFor(w); if (w.contact) setForm({ ...w.contact }); }} data-testid={`activation-contact-${w.id}`}>
                          <UserPlus className="h-3.5 w-3.5 mr-1" />{w.contact ? "Edit" : "Add"} Contact
                        </Button>
                      )}
                      {w.contact && !w.tenant_id && (
                        <Button size="sm" className="h-7 bg-red-700 hover:bg-red-800 text-white" disabled={busy === w.id} onClick={() => sendInvite(w.id)} data-testid={`activation-invite-${w.id}`}>
                          <Send className="h-3.5 w-3.5 mr-1" />{w.activation_status === "Invited" || w.activation_status === "Invite Expired" ? "Resend" : "Send Invite"}
                        </Button>
                      )}
                      {links[w.id] && (
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => copyLink(w.id)} data-testid={`activation-copy-${w.id}`}>
                          {copied === w.id ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add contact modal */}
      <Dialog open={!!contactFor} onOpenChange={(v) => !v && setContactFor(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="activation-contact-modal">
          <DialogHeader><DialogTitle>Primary contact — {contactFor?.school_name_snapshot}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="First name *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="contact-first" />
              <Input placeholder="Last name *" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="contact-last" />
            </div>
            <Input placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="contact-title" />
              <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <select value={form.school_role} onChange={(e) => setForm({ ...form, school_role: e.target.value })} className="h-10 w-full rounded-md border border-[#e5e7eb] px-3 text-sm" data-testid="contact-role">
              {SCHOOL_ROLES.map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setContactFor(null)}>Cancel</Button>
            <Button disabled={busy === "contact"} onClick={saveContact} className="bg-red-700 hover:bg-red-800 text-white" data-testid="contact-save">Save Contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk upload modal */}
      <Dialog open={bulkOpen} onOpenChange={(v) => !v && setBulkOpen(false)}>
        <DialogContent className="max-w-lg bg-white" data-testid="activation-bulk-modal">
          <DialogHeader><DialogTitle>Bulk upload school contacts</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-[12px] text-[#6b7280]">Paste CSV with a header row. Columns: <code>School Name, NCES School ID, Contact First Name, Contact Last Name, Contact Email, Contact Title, Contact Phone, School Role</code>. Matching is by NCES ID, then School Name + State.</p>
            <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={7} className="w-full rounded-md border border-[#e5e7eb] p-2 text-[12px] font-mono" placeholder="School Name,NCES School ID,Contact First Name,Contact Last Name,Contact Email,Contact Title,School Role" data-testid="bulk-textarea" />
            <label className="flex items-center gap-2 text-[13px]"><input type="checkbox" checked={sendAfter} onChange={(e) => setSendAfter(e.target.checked)} data-testid="bulk-send-after" /> Send invitations after import</label>
            {bulkResult && (
              <div className="rounded-lg bg-[#f9fafb] border border-[#e5e7eb] p-3 text-[13px]" data-testid="bulk-result">
                Created {bulkResult.created} · Updated {bulkResult.updated} · Skipped {bulkResult.skipped} · Invites sent {bulkResult.invites_sent}
                {bulkResult.errors?.length > 0 && <ul className="mt-1 text-[12px] text-amber-700 list-disc pl-4">{bulkResult.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}</ul>}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Close</Button>
            <Button disabled={busy === "bulk"} onClick={runBulk} className="bg-red-700 hover:bg-red-800 text-white" data-testid="bulk-import">{busy === "bulk" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
